import { useMemo } from "react";
import { useAuth } from "../../auth/authContext.tsx";
import { IdentitySinglePicker, type IdentitySinglePickerConfig } from "./identitySinglePicker.tsx";

interface OperatorItem {
  id: number;
  name: string;
}

interface OperatorSinglePickerProps {
  value: number;
  onChange: (id: number) => void;
  disabled?: boolean;
  placeholder?: string;
  onIdEnter?: () => void;
}

/**
 * Form field: pick a single operator by ID or by typing name.
 * Reads from `cache.CommonData.Common` (iOwnerOperatorID rows) via `useAuth`.
 */
export function OperatorSinglePicker({ value, onChange, disabled, placeholder, onIdEnter }: OperatorSinglePickerProps) {
  const { cache } = useAuth();

  const items = useMemo<OperatorItem[]>(() => {
    const common = (cache?.CommonData?.Common ?? []) as Record<string, unknown>[];
    const out: OperatorItem[] = [];
    for (const c of common) {
      const candidates = [c.ColumnName, c.TypeName, c.sName, c.columnName, c.typeName];
      let isOperatorRow = false;
      for (const cand of candidates) {
        if (typeof cand === "string" && cand.toLowerCase() === "iowneroperatorid") {
          isOperatorRow = true;
          break;
        }
      }
      if (!isOperatorRow) continue;
      const idRaw = c.Value ?? c.sFilterValue ?? c.value;
      if (idRaw == null || idRaw === "") continue;
      const id = parseInt(String(idRaw), 10);
      if (!Number.isFinite(id) || id <= 0) continue;
      const nameRaw = c.Title ?? c.sDisplayText ?? c.title;
      const name = nameRaw != null && nameRaw !== "" ? String(nameRaw) : String(id);
      out.push({ id, name });
    }
    // Retired operators (backend prefixes the name with ❌, NCHAR(10060))
    // sink to the bottom of the list per project-wide convention — see
    // pages/payap/operatorPicker.tsx and identityVariants/shared.tsx.
    out.sort((a, b) => {
      const ra = a.name.startsWith("❌");
      const rb = b.name.startsWith("❌");
      if (ra !== rb) return ra ? 1 : -1;
      return a.name.localeCompare(b.name);
    });
    return out;
  }, [cache]);

  const config = useMemo<IdentitySinglePickerConfig<OperatorItem>>(
    () => ({
      items,
      getId: (o) => o.id,
      nameField: "name",
      noun: "operator",
      matchFn: (o, q) =>
        String(o.id).includes(q) ||
        o.name.toLowerCase().includes(q),
      secondaryFn: () => "",
    }),
    [items],
  );

  return (
    <IdentitySinglePicker
      value={value}
      onChange={onChange}
      config={config}
      placeholder={placeholder}
      disabled={disabled}
      onIdEnter={onIdEnter}
    />
  );
}
